import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import API from "../../utils/apiCalling";
import { config } from "../../utils/apiUrl";


/*
|--------------------------------------------------------------------------
| PLATFORM API
|--------------------------------------------------------------------------
|
| Platform authentication must remain completely separate from:
|
| tenant accessToken
| normal admin authentication
|
|--------------------------------------------------------------------------
*/

const platformApi =
  new API({
    tokenKey:
      "platformAccessToken",
  });


/*
|--------------------------------------------------------------------------
| STORAGE KEYS
|--------------------------------------------------------------------------
*/

const PLATFORM_ACCESS_TOKEN =
  "platformAccessToken";

const PLATFORM_USER =
  "platformUser";


/*
|--------------------------------------------------------------------------
| STORAGE HELPERS
|--------------------------------------------------------------------------
*/

const getStoredToken = () => {

  if (
    typeof window ===
    "undefined"
  ) {
    return null;
  }

  return (
    localStorage.getItem(
      PLATFORM_ACCESS_TOKEN
    ) || null
  );

};


const getStoredUser = () => {

  if (
    typeof window ===
    "undefined"
  ) {
    return null;
  }

  try {

    const value =
      localStorage.getItem(
        PLATFORM_USER
      );

    if (!value) {
      return null;
    }

    return JSON.parse(
      value
    );

  } catch (error) {

    console.error(
      "[PlatformAuth] Unable to parse stored user:",
      error
    );

    return null;

  }

};


const savePlatformSession = ({
  accessToken,
  user,
}) => {

  if (
    typeof window ===
    "undefined"
  ) {
    return;
  }

  if (accessToken) {

    localStorage.setItem(
      PLATFORM_ACCESS_TOKEN,
      accessToken
    );

  }

  if (user) {

    localStorage.setItem(
      PLATFORM_USER,
      JSON.stringify(
        user
      )
    );

  }

};


const clearPlatformSession = () => {

  if (
    typeof window ===
    "undefined"
  ) {
    return;
  }

  localStorage.removeItem(
    PLATFORM_ACCESS_TOKEN
  );

  localStorage.removeItem(
    PLATFORM_USER
  );

};


/*
|--------------------------------------------------------------------------
| RESPONSE DATA HELPER
|--------------------------------------------------------------------------
|
| Supports:
|
| {
|   data: {...}
| }
|
| and direct response objects.
|--------------------------------------------------------------------------
*/

const getResponseData = (
  response
) => {

  return (
    response?.data ??
    response ??
    null
  );

};


/*
|--------------------------------------------------------------------------
| ERROR HELPER
|--------------------------------------------------------------------------
*/

const getErrorPayload = (
  error,
  fallbackMessage
) => {

  return {
    message:
      error?.message ||
      fallbackMessage,

    errorCode:
      error?.errorCode ||
      error?.code ||
      null,

    statusCode:
      error?.statusCode ||
      error?.status ||
      null,

    details:
      error?.details ||
      null,
  };

};


/*
|--------------------------------------------------------------------------
| PLATFORM LOGIN
|--------------------------------------------------------------------------
|
| POST /v1/platform/auth/login
|--------------------------------------------------------------------------
*/

export const platformLogin =
  createAsyncThunk(
    "platformAuth/login",

    async (
      credentials,
      {
        rejectWithValue,
      }
    ) => {

      try {

        /*
        |--------------------------------------------------------------------------
        | Login does not require Authorization header
        |--------------------------------------------------------------------------
        */

        const response =
          await platformApi.post(
            config.platformLogin,
            credentials,
            {
              skipAuth:
                true,
            }
          );

        const data =
          getResponseData(
            response
          );

        /*
        |--------------------------------------------------------------------------
        | Handle Common Backend Token Structures
        |--------------------------------------------------------------------------
        */

        const accessToken =
          data?.accessToken ||
          data?.token ||
          data?.tokens
            ?.accessToken ||
          null;

        const user =
          data?.user ||
          data?.admin ||
          data?.platformAdmin ||
          null;

        if (!accessToken) {

          return rejectWithValue({
            message:
              "Platform login succeeded but access token was not returned.",

            errorCode:
              "PLATFORM_TOKEN_MISSING",
          });

        }

        /*
        |--------------------------------------------------------------------------
        | Store Platform Session
        |--------------------------------------------------------------------------
        */

        savePlatformSession({
          accessToken,
          user,
        });

        return {
          accessToken,
          user,
          raw:
            data,
        };

      } catch (error) {

        return rejectWithValue(
          getErrorPayload(
            error,
            "Unable to login to platform."
          )
        );

      }

    }
  );


/*
|--------------------------------------------------------------------------
| GET PLATFORM PROFILE
|--------------------------------------------------------------------------
|
| GET /v1/platform/auth/me
|--------------------------------------------------------------------------
*/

export const fetchPlatformProfile =
  createAsyncThunk(
    "platformAuth/fetchProfile",

    async (
      _,
      {
        rejectWithValue,
      }
    ) => {

      try {

        const response =
          await platformApi.get(
            config.getPlatformProfile
          );

        const data =
          getResponseData(
            response
          );

        const user =
          data?.user ||
          data?.admin ||
          data?.platformAdmin ||
          data ||
          null;

        if (user) {

          savePlatformSession({
            user,
          });

        }

        return {
          user,
          raw:
            data,
        };

      } catch (error) {

        /*
        |--------------------------------------------------------------------------
        | Invalid / Expired Platform Session
        |--------------------------------------------------------------------------
        */

        if (
          error?.statusCode ===
            401 ||
          error?.status ===
            401
        ) {

          clearPlatformSession();

        }

        return rejectWithValue(
          getErrorPayload(
            error,
            "Unable to fetch platform profile."
          )
        );

      }

    }
  );


/*
|--------------------------------------------------------------------------
| INITIALIZE PLATFORM SESSION
|--------------------------------------------------------------------------
|
| Call when Platform layout/app loads.
|
| If no platform token exists, no API request is made.
|--------------------------------------------------------------------------
*/

export const initializePlatformAuth =
  createAsyncThunk(
    "platformAuth/initialize",

    async (
      _,
      {
        dispatch,
        rejectWithValue,
      }
    ) => {

      const accessToken =
        getStoredToken();

      if (!accessToken) {

        return {
          authenticated:
            false,

          accessToken:
            null,

          user:
            null,
        };

      }

      try {

        const result =
          await dispatch(
            fetchPlatformProfile()
          ).unwrap();

        return {
          authenticated:
            true,

          accessToken,

          user:
            result?.user ||
            null,
        };

      } catch (error) {

        clearPlatformSession();

        return rejectWithValue(
          error
        );

      }

    }
  );


/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialToken =
  getStoredToken();

const initialUser =
  getStoredUser();


const initialState = {

  /*
  |--------------------------------------------------------------------------
  | SESSION
  |--------------------------------------------------------------------------
  */

  accessToken:
    initialToken,

  user:
    initialUser,

  isAuthenticated:
    Boolean(
      initialToken
    ),


  /*
  |--------------------------------------------------------------------------
  | INITIALIZATION
  |--------------------------------------------------------------------------
  */

  initialized:
    false,

  initializing:
    false,


  /*
  |--------------------------------------------------------------------------
  | LOGIN
  |--------------------------------------------------------------------------
  */

  loginLoading:
    false,

  loginError:
    null,


  /*
  |--------------------------------------------------------------------------
  | PROFILE
  |--------------------------------------------------------------------------
  */

  profileLoading:
    false,

  profileError:
    null,

};


/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const platformAuthSlice =
  createSlice({

    name:
      "platformAuth",

    initialState,

    reducers: {

      /*
      |--------------------------------------------------------------------------
      | LOGOUT
      |--------------------------------------------------------------------------
      */

      platformLogout: (
        state
      ) => {

        clearPlatformSession();

        state.accessToken =
          null;

        state.user =
          null;

        state.isAuthenticated =
          false;

        state.initialized =
          true;

        state.initializing =
          false;

        state.loginLoading =
          false;

        state.profileLoading =
          false;

        state.loginError =
          null;

        state.profileError =
          null;

      },


      /*
      |--------------------------------------------------------------------------
      | CLEAR LOGIN ERROR
      |--------------------------------------------------------------------------
      */

      clearPlatformLoginError: (
        state
      ) => {

        state.loginError =
          null;

      },


      /*
      |--------------------------------------------------------------------------
      | CLEAR PROFILE ERROR
      |--------------------------------------------------------------------------
      */

      clearPlatformProfileError: (
        state
      ) => {

        state.profileError =
          null;

      },


      /*
      |--------------------------------------------------------------------------
      | SET PLATFORM USER
      |--------------------------------------------------------------------------
      */

      setPlatformUser: (
        state,
        action
      ) => {

        state.user =
          action.payload ||
          null;

        if (
          action.payload
        ) {

          savePlatformSession({
            user:
              action.payload,
          });

        }

      },

    },


    /*
    |--------------------------------------------------------------------------
    | EXTRA REDUCERS
    |--------------------------------------------------------------------------
    */

    extraReducers: (
      builder
    ) => {

      /*
      |--------------------------------------------------------------------------
      | LOGIN
      |--------------------------------------------------------------------------
      */

      builder

        .addCase(
          platformLogin.pending,
          (
            state
          ) => {

            state.loginLoading =
              true;

            state.loginError =
              null;

          }
        )

        .addCase(
          platformLogin.fulfilled,
          (
            state,
            action
          ) => {

            state.loginLoading =
              false;

            state.loginError =
              null;

            state.accessToken =
              action.payload
                ?.accessToken ||
              null;

            state.user =
              action.payload
                ?.user ||
              null;

            state.isAuthenticated =
              Boolean(
                action.payload
                  ?.accessToken
              );

            state.initialized =
              true;

          }
        )

        .addCase(
          platformLogin.rejected,
          (
            state,
            action
          ) => {

            state.loginLoading =
              false;

            state.loginError =
              action.payload ||
              {
                message:
                  action.error
                    ?.message ||
                  "Platform login failed.",
              };

            state.accessToken =
              null;

            state.user =
              null;

            state.isAuthenticated =
              false;

            state.initialized =
              true;

          }
        );


      /*
      |--------------------------------------------------------------------------
      | PROFILE
      |--------------------------------------------------------------------------
      */

      builder

        .addCase(
          fetchPlatformProfile.pending,
          (
            state
          ) => {

            state.profileLoading =
              true;

            state.profileError =
              null;

          }
        )

        .addCase(
          fetchPlatformProfile.fulfilled,
          (
            state,
            action
          ) => {

            state.profileLoading =
              false;

            state.profileError =
              null;

            state.user =
              action.payload
                ?.user ||
              null;

            state.isAuthenticated =
              Boolean(
                state.accessToken ||
                getStoredToken()
              );

          }
        )

        .addCase(
          fetchPlatformProfile.rejected,
          (
            state,
            action
          ) => {

            state.profileLoading =
              false;

            state.profileError =
              action.payload ||
              {
                message:
                  action.error
                    ?.message ||
                  "Unable to fetch platform profile.",
              };

            /*
            |--------------------------------------------------------------------------
            | If token disappeared because of 401, clear Redux session too.
            |--------------------------------------------------------------------------
            */

            if (
              !getStoredToken()
            ) {

              state.accessToken =
                null;

              state.user =
                null;

              state.isAuthenticated =
                false;

            }

          }
        );


      /*
      |--------------------------------------------------------------------------
      | INITIALIZE
      |--------------------------------------------------------------------------
      */

      builder

        .addCase(
          initializePlatformAuth.pending,
          (
            state
          ) => {

            state.initializing =
              true;

          }
        )

        .addCase(
          initializePlatformAuth.fulfilled,
          (
            state,
            action
          ) => {

            state.initializing =
              false;

            state.initialized =
              true;

            state.accessToken =
              action.payload
                ?.accessToken ||
              null;

            state.user =
              action.payload
                ?.user ||
              null;

            state.isAuthenticated =
              Boolean(
                action.payload
                  ?.authenticated
              );

          }
        )

        .addCase(
          initializePlatformAuth.rejected,
          (
            state
          ) => {

            state.initializing =
              false;

            state.initialized =
              true;

            state.accessToken =
              null;

            state.user =
              null;

            state.isAuthenticated =
              false;

          }
        );

    },

  });


/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
  platformLogout,
  clearPlatformLoginError,
  clearPlatformProfileError,
  setPlatformUser,
} =
  platformAuthSlice.actions;


/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
*/

export const selectPlatformAuth = (
  state
) =>
  state.platformAuth;


export const selectPlatformUser = (
  state
) =>
  state.platformAuth
    ?.user ||
  null;


export const selectPlatformAccessToken = (
  state
) =>
  state.platformAuth
    ?.accessToken ||
  null;


export const selectIsPlatformAuthenticated = (
  state
) =>
  Boolean(
    state.platformAuth
      ?.isAuthenticated
  );


export const selectPlatformAuthInitialized = (
  state
) =>
  Boolean(
    state.platformAuth
      ?.initialized
  );


export const selectPlatformLoginLoading = (
  state
) =>
  Boolean(
    state.platformAuth
      ?.loginLoading
  );


export const selectPlatformLoginError = (
  state
) =>
  state.platformAuth
    ?.loginError ||
  null;


/*
|--------------------------------------------------------------------------
| REDUCER
|--------------------------------------------------------------------------
*/

export default platformAuthSlice.reducer;